import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { EstadoPromesa, montoPromesa } from "@/pages/_sales/PromesasPago";
import {
  ArrowLeftIcon, FileIcon, DownloadSimpleIcon, CalendarBlankIcon, CheckCircleIcon, ProhibitIcon, ClockCounterClockwiseIcon,
} from "@phosphor-icons/react";

const kb = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);
const fh = (iso) => (iso ? new Date(iso).toLocaleString("es-HN", { dateStyle: "medium", timeStyle: "short" }) : "");
const inp = "w-full min-h-[44px] bg-app border border-border rounded-md px-3 py-2 text-sm";
const ACCIONES = { creada: "Creada", reprogramada: "Reprogramada", cumplida: "Cumplida", cancelada: "Cancelada", pendiente: "Reabierta" };

const Dato = ({ l, children }) => (
  <div><div className="text-[11px] uppercase tracking-wide text-text-muted">{l}</div><div className="text-sm mt-0.5">{children}</div></div>
);

/** Detalle de la promesa: el mismo registro que abre el Calendario. */
export default function PromesaDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const eff = user?.effective || {};
  const validador = Boolean(eff.is_admin || (eff.roles || []).includes("delegado"));
  const [p, setP] = useState(null);
  const [error, setError] = useState("");
  const [accion, setAccion] = useState("");             // reprogramar | cumplida | cancelada
  const [f, setF] = useState({ fecha: "", motivo: "", pago_id: "", nota: "" });
  const [pagos, setPagos] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const cargar = useCallback(() => api.get(`/promesas-pago/${id}`).then(({ data }) => setP(data)).catch((e) => setError(apiErr(e))), [id]);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    if (accion === "cumplida" && pagos === null) api.get(`/promesas-pago/${id}/pagos-cliente`).then(({ data }) => setPagos(data)).catch(() => setPagos([]));
  }, [accion, pagos, id]);

  const descargar = async (a) => {
    try {
      const { data } = await api.get(`/promesas-pago/files/${a.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const el = document.createElement("a");
      el.href = url; el.download = a.nombre; el.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error("No se pudo descargar el archivo"); }
  };

  const enviar = async (ev) => {
    ev.preventDefault();
    if (enviando) return;
    setEnviando(true);
    try {
      const { data } = accion === "reprogramar"
        ? await api.post(`/promesas-pago/${id}/reprogramar`, { fecha: f.fecha, motivo: f.motivo })
        : await api.post(`/promesas-pago/${id}/estado`, { estado: accion, nota: f.nota, pago_id: f.pago_id || null });
      setP(data); setAccion(""); setF({ fecha: "", motivo: "", pago_id: "", nota: "" });
      toast.success(accion === "reprogramar" ? "Promesa reprogramada" : accion === "cumplida" ? "Promesa marcada como cumplida" : "Promesa cancelada");
    } catch (e) { toast.error(apiErr(e)); } finally { setEnviando(false); }
  };

  if (error) return <Layout title="Promesa de pago"><div className="text-sm text-red-300">{error}</div></Layout>;
  if (!p) return <Layout title="Promesa de pago"><div className="text-sm text-text-muted">Cargando…</div></Layout>;
  const abierta = !["cumplida", "cancelada"].includes(p.estado);
  const btn = "min-h-[44px] px-4 rounded-md border text-sm flex items-center gap-2";

  return (
    <Layout title="Promesa de pago" subtitle={p.cliente_nombre}>
      <div className="max-w-4xl space-y-4" data-testid="promesa-detalle">
        <div className="flex flex-wrap items-center gap-2 justify-between">
          <button type="button" onClick={() => navigate("/ventas?tab=promesas")} className="min-h-[44px] text-sm text-text-secondary hover:text-text-primary flex items-center gap-1.5">
            <ArrowLeftIcon size={16} /> Promesas de pago</button>
          <button type="button" onClick={() => navigate("/calendario")} className="min-h-[44px] text-sm text-text-secondary hover:text-text-primary flex items-center gap-1.5">
            <CalendarBlankIcon size={16} /> Ver en el Calendario</button>
        </div>

        <section className="bg-card border border-border/60 rounded-xl p-4 sm:p-5 space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div><div className="font-heading text-lg">{p.cliente_nombre}</div><div className="text-xs text-text-muted">{p.cliente_codigo}</div></div>
            <span data-testid="promesa-estado"><EstadoPromesa e={p.estado_visible} /></span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Dato l="Fecha comprometida"><b data-testid="promesa-fecha-actual">{fmtDate(p.fecha)}</b>{p.fecha_original && p.fecha_original !== p.fecha && <span className="block text-[11px] text-text-muted">Original: {fmtDate(p.fecha_original)}</span>}</Dato>
            <Dato l="Monto comprometido"><b>{montoPromesa(p)}</b></Dato>
            <Dato l="Responsable">{p.responsable_nombre || p.created_by}<span className="block text-[11px] text-text-muted">{fh(p.created_at)}</span></Dato>
          </div>
          <Dato l="Motivo o compromiso"><p className="whitespace-pre-wrap text-text-secondary">{p.nota}</p></Dato>
          {p.pago_numero && <Dato l="Sustento de cumplimiento">Recibo <b>{p.pago_numero}</b></Dato>}
          {p.validada_por && <Dato l="Validada por">{p.validada_por}</Dato>}
          <p className="text-[11px] text-text-muted">La promesa no registra pagos ni cambia el saldo del cliente.</p>
        </section>

        <section className="bg-card border border-border/60 rounded-xl p-4 sm:p-5">
          <h3 className="text-sm font-semibold mb-2">Facturas relacionadas</h3>
          {(p.facturas || []).length === 0 ? <p className="text-xs text-text-muted">Sin facturas relacionadas.</p> : (
            <ul className="divide-y divide-border/60">
              {p.facturas.map((x) => (
                <li key={x.id} className="py-2 flex flex-wrap justify-between gap-2 text-sm">
                  <span><b className="font-mono">{x.numero}</b> <span className="text-xs text-text-muted">· vence {fmtDate(x.fecha_vencimiento)}</span></span>
                  <span className="text-xs">Saldo actual <b>{fmtL(x.saldo_pendiente)}</b></span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="bg-card border border-border/60 rounded-xl p-4 sm:p-5">
          <h3 className="text-sm font-semibold mb-2">Archivos adjuntos</h3>
          {(p.adjuntos || []).length === 0 ? <p className="text-xs text-text-muted">Sin archivos.</p> : (
            <ul className="space-y-2" data-testid="promesa-adjuntos">
              {p.adjuntos.map((a) => (
                <li key={a.id} className="flex items-center gap-3 border border-border rounded-md p-2">
                  <FileIcon size={20} className="text-text-muted shrink-0" aria-hidden="true" />
                  <div className="flex-1 min-w-0"><div className="text-sm truncate">{a.nombre}</div><div className="text-[11px] text-text-muted">{kb(a.size)} · {a.subido_por}</div></div>
                  <button type="button" onClick={() => descargar(a)} aria-label={`Descargar ${a.nombre}`} className="min-h-[44px] min-w-[44px] flex items-center justify-center text-text-secondary hover:text-text-primary">
                    <DownloadSimpleIcon size={18} /></button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {abierta && (
          <section className="bg-card border border-border/60 rounded-xl p-4 sm:p-5 space-y-3">
            <h3 className="text-sm font-semibold">Seguimiento</h3>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => setAccion("reprogramar")} aria-pressed={accion === "reprogramar"} data-testid="promesa-reprogramar"
                className={`${btn} ${accion === "reprogramar" ? "border-focus text-text-primary" : "border-border text-text-secondary"}`}><CalendarBlankIcon size={16} /> Reprogramar</button>
              <button type="button" onClick={() => setAccion("cumplida")} aria-pressed={accion === "cumplida"} data-testid="promesa-cumplida"
                className={`${btn} ${accion === "cumplida" ? "border-agro-green text-agro-green" : "border-border text-text-secondary"}`}><CheckCircleIcon size={16} /> Marcar cumplida</button>
              <button type="button" onClick={() => setAccion("cancelada")} aria-pressed={accion === "cancelada"} data-testid="promesa-cancelar"
                className={`${btn} ${accion === "cancelada" ? "border-red-400 text-red-300" : "border-border text-text-secondary"}`}><ProhibitIcon size={16} /> Cancelar promesa</button>
            </div>
            {accion && (
              <form onSubmit={enviar} className="border border-border rounded-lg p-3 space-y-3" data-testid="promesa-accion-form">
                {accion === "reprogramar" && <>
                  <label className="block"><span className="block text-xs font-semibold text-text-secondary mb-1">Nueva fecha comprometida *</span>
                    <input type="date" required value={f.fecha} onChange={(e) => setF({ ...f, fecha: e.target.value })} className={inp} data-testid="promesa-nueva-fecha" /></label>
                  <label className="block"><span className="block text-xs font-semibold text-text-secondary mb-1">Motivo de la reprogramación *</span>
                    <textarea required minLength={3} rows={3} value={f.motivo} onChange={(e) => setF({ ...f, motivo: e.target.value })} className={inp} data-testid="promesa-motivo" /></label>
                </>}
                {accion === "cumplida" && <>
                  <p className="text-xs text-text-secondary">Se sustenta con un pago registrado del cliente{validador ? " o con tu validación como autorizador" : ""}.</p>
                  <label className="block"><span className="block text-xs font-semibold text-text-secondary mb-1">Recibo de pago del cliente</span>
                    <select value={f.pago_id} onChange={(e) => setF({ ...f, pago_id: e.target.value })} className={inp} data-testid="promesa-pago">
                      <option value="">{pagos === null ? "Cargando recibos…" : pagos.length ? "— Elige el recibo —" : "Sin recibos desde la promesa"}</option>
                      {(pagos || []).map((x) => <option key={x.id} value={x.id}>{x.numero_recibo} · {fmtDate(x.fecha)} · {fmtL(x.monto_total ?? x.monto)}</option>)}
                    </select></label>
                  <label className="block"><span className="block text-xs font-semibold text-text-secondary mb-1">{f.pago_id || !validador ? "Nota (opcional)" : "Validación que sustenta el cumplimiento *"}</span>
                    <textarea rows={2} value={f.nota} onChange={(e) => setF({ ...f, nota: e.target.value })} className={inp} data-testid="promesa-nota-estado" /></label>
                  {!f.pago_id && !validador && <p className="text-xs text-agro-yellow">Sin recibo, la validación la debe hacer el Administrador o el Delegado.</p>}
                </>}
                {accion === "cancelada" && (
                  <label className="block"><span className="block text-xs font-semibold text-text-secondary mb-1">Motivo de la cancelación *</span>
                    <textarea required rows={2} value={f.nota} onChange={(e) => setF({ ...f, nota: e.target.value })} className={inp} data-testid="promesa-nota-estado" /></label>
                )}
                <div className="flex flex-wrap justify-end gap-2">
                  <button type="button" onClick={() => setAccion("")} className={`${btn} border-border`}>Cerrar</button>
                  <button type="submit" disabled={enviando || (accion === "cumplida" && !f.pago_id && (!validador || !f.nota.trim()))} data-testid="promesa-accion-guardar"
                    className="min-h-[44px] px-5 bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md text-sm disabled:opacity-50">
                    {enviando ? "Guardando…" : "Guardar"}</button>
                </div>
              </form>
            )}
          </section>
        )}

        <section className="bg-card border border-border/60 rounded-xl p-4 sm:p-5">
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><ClockCounterClockwiseIcon size={16} /> Historial</h3>
          <ol className="space-y-2" data-testid="promesa-historial">
            {[...(p.historial || [])].reverse().map((h, i) => (
              <li key={i} className="border-l-2 border-border pl-3 text-sm">
                <div><b>{ACCIONES[h.accion] || h.accion}</b> <span className="text-[11px] text-text-muted">· {fh(h.fecha)} · {h.usuario}</span></div>
                {h.accion === "reprogramada" && <div className="text-xs">De <b>{fmtDate(h.fecha_anterior)}</b> a <b>{fmtDate(h.fecha_nueva)}</b></div>}
                {h.detalle && <div className="text-xs text-text-secondary">{h.detalle}</div>}
              </li>
            ))}
          </ol>
        </section>
      </div>
    </Layout>
  );
}
